import {
  type TagProps,
  type DropdownOption,
  ChakraFlex,
  ChakraBox,
  Text,
  TagColor,
  TagCell,
  StatusCell,
  useToastMessage,
  FAIconName,
  ModalV2,
  FireflyButton,
  FormLabel,
  UniversalSelect,
} from '@equip.health/ui';
import { useCallback, useState, useEffect, useMemo } from 'react';
import {
  useGetStatusOptionsQuery,
  useUpdateGroupStatusMutation,
  useGetArchiveReasonsListQuery,
} from '~/store/services/groupClass';

interface GroupClassDetailsDrawerHeaderProps {
  isReadOnlyView?: boolean;
  title: string;
  groupId: string;
  currentStatus: GroupStatusType;
  onStatusChanged?: () => void;
}

const STATUS_COLOR_MAP: Record<GroupStatusType, TagColor> = {
  LIVE: TagColor.green,
  DRAFT: TagColor.marigold,
  ARCHIVED: TagColor.gray,
};

const STATUS_LABEL_MAP: Record<GroupStatusType, string> = {
  LIVE: 'Live',
  DRAFT: 'Draft',
  ARCHIVED: 'Archived',
};

const TOP_LEVEL_ID = 'group-class-details-drawer-header';

const GroupClassDetailsDrawerHeader = ({
  title,
  groupId,
  currentStatus,
  onStatusChanged,
  isReadOnlyView,
}: GroupClassDetailsDrawerHeaderProps) => {
  const { createToast } = useToastMessage();
  const { data: statusOptionsResponse } = useGetStatusOptionsQuery();
  const [updateGroupStatus, { isLoading: isUpdatingStatus }] =
    useUpdateGroupStatusMutation();

  const [selectedStatus, setSelectedStatus] = useState<string>(
    STATUS_LABEL_MAP[currentStatus],
  );
  const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);
  const [selectedArchiveReason, setSelectedArchiveReason] =
    useState<DropdownOption | null>(null);
  const [pendingStatusCode, setPendingStatusCode] =
    useState<GroupStatusType | null>(null);

  const { data: archiveReasonsResponse } = useGetArchiveReasonsListQuery(
    undefined,
    { skip: !isArchiveModalOpen },
  );

  useEffect(() => {
    setSelectedStatus(STATUS_LABEL_MAP[currentStatus]);
  }, [currentStatus]);

  const archiveReasons = useMemo(
    () =>
      archiveReasonsResponse?.data.map(
        (reason: GroupClassArchivalReasonResponseItem) => ({
          label: reason.archiveReason,
          value: reason.archiveReasonCode,
        }),
      ) ?? [],
    [archiveReasonsResponse],
  );

  const statusOptions = useMemo<TagProps[]>(
    () =>
      statusOptionsResponse
        ?.filter((item) => {
          if (currentStatus === 'ARCHIVED') return false;
          if (currentStatus === 'LIVE' && item.code === 'DRAFT') {
            return false;
          }

          return true;
        })
        .map((item) => ({
          color: STATUS_COLOR_MAP[item.code as GroupStatusType],
          id: `${TOP_LEVEL_ID}__status-option-${item.code}`,
          value: item.status,
        })) ?? [],
    [statusOptionsResponse, currentStatus],
  );

  const isStatusEditable = currentStatus !== 'ARCHIVED';

  const handleArchiveModalClose = useCallback(() => {
    setIsArchiveModalOpen(false);
    setSelectedArchiveReason(null);
    setPendingStatusCode(null);
  }, []);

  const handleArchiveConfirm = () => {
    if (!selectedArchiveReason || !pendingStatusCode) return;

    updateGroupStatus({
      groupId,
      groupStatus: pendingStatusCode,
      archiveReason: selectedArchiveReason.value,
    })
      .unwrap()
      .then(() => {
        setSelectedStatus(STATUS_LABEL_MAP[pendingStatusCode]);
        createToast({
          icon: FAIconName.checkRegular,
          id: `${TOP_LEVEL_ID}__status-update-success`,
          text: 'Group archived successfully',
          variant: 'success',
        });
        handleArchiveModalClose();
        onStatusChanged?.();
      })
      .catch(() => {
        createToast({
          icon: FAIconName.exclamationTriangleRegular,
          id: `${TOP_LEVEL_ID}__status-update-error`,
          text: 'Failed to archive group. Please try again.',
          variant: 'critical',
        });
      });
  };

  const updateStatus = useCallback(
    (optionId: string) => {
      const selectedOption = statusOptions.find(
        (option) => option.id === optionId,
      );
      if (!selectedOption) return;

      const newStatusCode = statusOptionsResponse?.find(
        (item) => item.status === selectedOption.value,
      )?.code as GroupStatusType;

      if (!newStatusCode || newStatusCode === currentStatus) return;

      if (newStatusCode === 'ARCHIVED') {
        setPendingStatusCode(newStatusCode);
        setIsArchiveModalOpen(true);
        return;
      }

      updateGroupStatus({
        groupId,
        groupStatus: newStatusCode,
      })
        .unwrap()
        .then(() => {
          setSelectedStatus(selectedOption.value);
          createToast({
            icon: FAIconName.checkRegular,
            id: `${TOP_LEVEL_ID}__status-update-success`,
            text: `Group status updated to ${selectedOption.value}`,
            variant: 'success',
          });
          onStatusChanged?.();
        })
        .catch(() => {
          createToast({
            icon: FAIconName.exclamationTriangleRegular,
            id: `${TOP_LEVEL_ID}__status-update-error`,
            text: 'Failed to update group status. Please try again.',
            variant: 'critical',
          });
        });
    },
    [
      statusOptions,
      statusOptionsResponse,
      currentStatus,
      groupId,
      updateGroupStatus,
      createToast,
      onStatusChanged,
    ],
  );

  return (
    <>
      <ChakraFlex
        alignItems="center"
        id={TOP_LEVEL_ID}
        justifyContent="space-between"
        paddingY="3px"
        height="45px"
      >
        <Text
          color="neutral.primary"
          id={`${TOP_LEVEL_ID}__title`}
          variant="h4"
        >
          {title}
        </Text>
        <ChakraBox marginRight="44px" zIndex={2}>
          {/* Here added zIndex to avoid overlapping of avatar in below section card*/}
          {isStatusEditable && !isReadOnlyView ? (
            <StatusCell
              id={`${TOP_LEVEL_ID}__status`}
              status={selectedStatus}
              statusOptions={statusOptions}
              onOptionClick={updateStatus}
            />
          ) : (
            <TagCell
              id={`${TOP_LEVEL_ID}__status`}
              color={STATUS_COLOR_MAP[currentStatus]}
              value={selectedStatus}
            />
          )}
        </ChakraBox>
      </ChakraFlex>

      <ModalV2
        id={`${TOP_LEVEL_ID}__archive-modal`}
        isOpen={isArchiveModalOpen}
        onClose={handleArchiveModalClose}
        size="md"
        title="Archive group"
      >
        <Text>
          Are you sure you want to archive this group? All sections will be{' '}
          deleted and all registered users will be unregistered and have all{' '}
          future events removed from their schedule.
        </Text>
        <ChakraBox gap="6px" marginTop="24px">
          <FormLabel
            id={`${TOP_LEVEL_ID}__archive-reason-label`}
            label="Archive reason"
            shouldShowAsteriskWhenRequired
          />
        </ChakraBox>
        <ChakraBox marginTop="6px">
          <UniversalSelect
            id={`${TOP_LEVEL_ID}__archive-reasons-dropdown`}
            isSingleSelect
            modalId={`${TOP_LEVEL_ID}__archive-modal`}
            onChange={setSelectedArchiveReason}
            options={archiveReasons}
            placeholder="Please choose"
          />
        </ChakraBox>
        <ChakraFlex justifyContent="space-between" marginTop="24px">
          <FireflyButton
            id={`${TOP_LEVEL_ID}__back-button`}
            isDisabled={isUpdatingStatus}
            leftIcon={FAIconName.arrowLeftRegular}
            onClick={handleArchiveModalClose}
            variant="secondary"
          >
            Back
          </FireflyButton>
          <FireflyButton
            id={`${TOP_LEVEL_ID}__confirm-button`}
            isDisabled={!selectedArchiveReason || isUpdatingStatus}
            isLoading={isUpdatingStatus}
            leftIcon={FAIconName.checkRegular}
            onClick={handleArchiveConfirm}
            variant="accent.critical"
          >
            Archive group
          </FireflyButton>
        </ChakraFlex>
      </ModalV2>
    </>
  );
};

export default GroupClassDetailsDrawerHeader;
